import { useState } from "react";
import Box from "@mui/material/Box";
import TablePagination from "@mui/material/TablePagination";
import { BdcBillingRow } from "../../../types/bdc.type";
import { baht, thaiDate } from "../format";
import Tag from "../Tag";

const PAGE_SIZE = 100;

/**
 * The rows of a ใบวางบิล in the order and columns of the IFS file (NO · ว-ด-ป · เลขที่ · รหัสลูกค้า ·
 * มูลค่า · VAT · ยอดเงิน · เอกสารออกแทน · DC · เล่ม · เล่มที่). A month runs to ~1,000 rows, so the
 * table pages itself (100 a page) rather than drawing them all.
 */
export default function BillingRowsTable({
  rows,
  totals,
  testId,
}: Readonly<{
  rows: BdcBillingRow[];
  totals?: { subtotal: number; vat_amount: number; grand_total: number };
  testId?: string;
}>) {
  const [page, setPage] = useState(0);
  const lastPage = Math.max(0, Math.ceil(rows.length / PAGE_SIZE) - 1);
  const current = Math.min(page, lastPage);
  const shown = rows.slice(current * PAGE_SIZE, (current + 1) * PAGE_SIZE);

  return (
    <>
      <Box sx={{ overflow: "auto", maxHeight: 520 }}>
        <table className="bdc-t" data-testid={testId}>
          <thead>
            <tr>
              <th className="n">NO</th>
              <th>ว-ด-ป</th>
              <th>เลขที่</th>
              <th>รหัสลูกค้า</th>
              <th className="n">มูลค่าสินค้า</th>
              <th className="n">VAT</th>
              <th className="n">ยอดเงิน</th>
              <th>เอกสารออกแทน</th>
              <th>DC</th>
              <th>เล่ม</th>
              <th>เล่มที่</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((row) => (
              <tr key={`${row.no}-${row.doc_no}`} style={row.doc_type === "credit_note" ? { background: "#FFF8F8" } : undefined}>
                <td className="n">{row.no}</td>
                <td>{thaiDate(row.doc_date)}</td>
                <td className="mono">
                  {row.doc_no} {row.doc_type === "credit_note" && <Tag tone="crit">CN</Tag>}
                </td>
                <td className="mono">{row.ar_customer_code ?? <Tag tone="crit">ไม่มีรหัส</Tag>}</td>
                <td className="n">{baht(row.subtotal)}</td>
                <td className="n">{baht(row.vat_amount)}</td>
                <td className="n">
                  <b>{baht(row.grand_total)}</b>
                </td>
                <td className="mono">{row.po_number ?? ""}</td>
                <td className="mono">{row.billing_label ?? ""}</td>
                <td className="mono">{row.book_code ?? ""}</td>
                <td className="mono">{row.book_no ?? ""}</td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={11} className="sub" style={{ textAlign: "center", padding: 16 }}>
                  ไม่มีเอกสาร
                </td>
              </tr>
            )}
          </tbody>
          {totals && rows.length > 0 && (
            <tfoot>
              <tr>
                <td colSpan={4}>รวม</td>
                <td className="n">{baht(totals.subtotal)}</td>
                <td className="n">{baht(totals.vat_amount)}</td>
                <td className="n">{baht(totals.grand_total)}</td>
                <td colSpan={4} />
              </tr>
            </tfoot>
          )}
        </table>
      </Box>
      {rows.length > PAGE_SIZE && (
        <TablePagination
          component="div"
          count={rows.length}
          page={current}
          rowsPerPage={PAGE_SIZE}
          rowsPerPageOptions={[PAGE_SIZE]}
          onPageChange={(_e, next) => setPage(next)}
        />
      )}
    </>
  );
}
